import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { PageSection, SectionSubTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect, useMemo, useRef } from 'react';
import { TRACKING_SOURCES, trackTermSelectorInteraction } from '../../utils/tracking';
import { PublicHubMerchCardLayout } from './PublicHubMerchCardLayout';
import { PublicMerchCardLayoutUI } from './PublicMerchCardLayoutUI';
import { PublicMerchCardPrice } from './PublicMerchCardPrice';
import { PublicMerchCardSkeleton } from './PublicMerchCardSkeleton';
import { PublicTermSelector } from './PublicTermSelector';
import { getMerchCopyForProductGroup } from './productGroupMerchCopy';
import { resolveCardPricing } from './resolveCardPricing';
import { PublicMerchCardCTAs } from './slots/PublicMerchCardCTAs';
import { PublicMerchCardCredits } from './slots/PublicMerchCardCredits';
import { PublicMerchCardFeatures } from './slots/PublicMerchCardFeatures';
import { PublicMerchCardFooter } from './slots/PublicMerchCardFooter';
import { PublicMerchCardSeats } from './slots/PublicMerchCardSeats';
import { PublicMerchCardTitle } from './slots/PublicMerchCardTitle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getTermSelectorSource(productLevel) {
  if (productLevel === 'STARTER') {
    return 'starter-customer-platform-upsell-merch-card';
  }
  return 'merch-card';
}
function formatDiscountShown(discountPercentage) {
  if (discountPercentage === 10) return '10PercentDiscount';
  return 'PercentDiscount';
}
function getPublicMotions(motions) {
  return motions.filter(m => typeof m === 'string');
}
export const PublicHubMerchCards = ({
  cards,
  productGroup,
  status,
  currencyCode,
  selectedTerm,
  setSelectedTerm,
  skeletonCount = 3,
  screen,
  belowCards,
  renderSecondaryCTA
}) => {
  const hasTrackedTermViewRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedTermViewRef.current && status === 'ready' && cards.length) {
      const firstCardWithTerms = cards.find(c => c.availableTerms['ANNUAL'] && c.availableTerms['MONTHLY']);
      if (firstCardWithTerms) {
        hasTrackedTermViewRef.current = true;
        const viewPricing = resolveCardPricing(firstCardWithTerms, selectedTerm);
        trackTermSelectorInteraction({
          action: 'view',
          term: String(selectedTerm).toLowerCase(),
          trackingSource: TRACKING_SOURCES.MERCH_CARD,
          screen,
          productNames: cards.map(c => c.productApiName),
          paymentTerm: viewPricing.paymentTerm,
          commitTerm: viewPricing.commitmentTerm,
          discountShown: formatDiscountShown(viewPricing.discountPercentage)
        });
      }
    }
  }, [status, cards, selectedTerm, screen]);
  const {
    subheadingsByLevel,
    lowerTierMessagesByLevel
  } = useMemo(() => getMerchCopyForProductGroup(productGroup), [productGroup]);
  const {
    subheadingsByLevel: platformSubheadingsByLevel
  } = useMemo(() => getMerchCopyForProductGroup('PLATFORM'), []);
  const resolvedPricings = useMemo(() => cards.map(card => resolveCardPricing(card, selectedTerm)), [cards, selectedTerm]);
  if (status === 'error') {
    return /*#__PURE__*/_jsx(PageSection, {
      children: /*#__PURE__*/_jsx(View, {
        padding: "600",
        children: /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "publicPricingPage.merchCard.error.loadFailed"
          })
        })
      })
    });
  }
  if (status === 'loading') {
    return /*#__PURE__*/_jsx(PageSection, {
      children: /*#__PURE__*/_jsx(PublicHubMerchCardLayout, {
        columns: skeletonCount,
        children: Array.from({
          length: skeletonCount
        }, (_, i) => /*#__PURE__*/_jsx(PublicMerchCardSkeleton, {
          columnStart: i + 1
        }, i))
      })
    });
  }
  return /*#__PURE__*/_jsxs(PageSection, {
    children: [/*#__PURE__*/_jsx(PublicHubMerchCardLayout, {
      columns: cards.length,
      children: cards.map((card, index) => {
        const pricing = resolvedPricings[index];
        const columnStart = index + 1;
        const subheading = card.productLevel ? card.productLevel === 'STARTER' ? platformSubheadingsByLevel[card.productLevel] : subheadingsByLevel[card.productLevel] : undefined;
        const lowerTierMessage = card.productLevel ? lowerTierMessagesByLevel[card.productLevel] : undefined;
        return /*#__PURE__*/_jsx(PublicMerchCardLayoutUI, {
          "data-selenium-id": `${card.productApiName}-merch-card`,
          columnStart: columnStart,
          Title: /*#__PURE__*/_jsx(PublicMerchCardTitle, {
            card: card
          }),
          Subheading: /*#__PURE__*/_jsx(SectionSubTitle, {
            children: subheading || ' '
          }),
          Pricing: pricing.isFree ? /*#__PURE__*/_jsx(PublicMerchCardPrice, {
            monthlyPrice: 0,
            currencyCode: currencyCode,
            selectedTerm: "MONTHLY",
            productApiName: card.productApiName
          }) : /*#__PURE__*/_jsx(PublicMerchCardPrice, {
            monthlyPrice: pricing.monthlyPrice,
            listPrice: pricing.listPrice,
            currencyCode: currencyCode,
            selectedTerm: pricing.effectiveTerm,
            showPerMonthPerSeat: pricing.isPerSeat,
            productApiName: card.productApiName
          }),
          Term: pricing.hasAnnual && pricing.hasMonthly && !pricing.isFree ? /*#__PURE__*/_jsx(PublicTermSelector, {
            selectedTerm: selectedTerm,
            onTermChange: newTerm => {
              const newPricing = resolveCardPricing(card, newTerm);
              trackTermSelectorInteraction({
                action: 'click',
                term: String(newTerm).toLowerCase(),
                trackingSource: TRACKING_SOURCES.MERCH_CARD,
                screen,
                productNames: [card.productApiName],
                paymentTerm: newPricing.paymentTerm,
                commitTerm: newPricing.commitmentTerm,
                discountShown: formatDiscountShown(pricing.discountPercentage)
              });
              setSelectedTerm(newTerm);
            },
            availableTerms: card.availableTerms,
            discountPercentage: pricing.discountPercentage,
            dataSeleniumId: `term-selector-${getTermSelectorSource(card.productLevel)}-${card.productApiName}`
          }) : undefined,
          BelowPrice: pricing.isFree ? /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "publicPricingPage.merchCard.features.freeSubtext"
            })
          }) : /*#__PURE__*/_jsx(PublicMerchCardSeats, {
            card: card,
            currencyCode: currencyCode,
            isPerSeat: pricing.isPerSeat,
            additionalSeatPrice: pricing.additionalSeatPrice
          }),
          CTAs: /*#__PURE__*/_jsx(PublicMerchCardCTAs, {
            card: card,
            currencyCode: currencyCode,
            paymentTerm: pricing.paymentTerm,
            commitmentTerm: pricing.commitmentTerm,
            motions: getPublicMotions(card.purchaseMotions),
            screen: screen,
            secondaryCTA: renderSecondaryCTA === null || renderSecondaryCTA === void 0 ? void 0 : renderSecondaryCTA(card)
          }),
          BelowCTA: /*#__PURE__*/_jsx(PublicMerchCardCredits, {
            card: card,
            commitmentTerm: pricing.commitmentTerm === 'ANNUAL' ? 12 : 1,
            paymentTerm: pricing.paymentTerm === 'ANNUAL' ? 12 : 1,
            showMarketingContactsModal: productGroup === 'MARKETING' || productGroup === 'HUBSPOT_FOR_MARKETERS'
          }),
          Features: /*#__PURE__*/_jsx(PublicMerchCardFeatures, {
            card: card,
            lowerTierMessage: lowerTierMessage
          }),
          Footer: /*#__PURE__*/_jsx(PublicMerchCardFooter, {
            card: card,
            currencyCode: currencyCode
          })
        }, card.productApiName);
      })
    }), belowCards]
  });
};